<template>
  <div class="description-container">
    <BaseTextInput
      name="title"
      class="description-container__title-input"
      :data-cy="'productTitle-' + selectedLocaleCode"
      :label="$t('TITLE')"
      :value="titles[selectedLocaleCode]"
      :placeholder="$t('TITLE')"
      :ai="hasCompanyAi"
      :disabled="isDisabled"
      @onAi="openAiTitleModal"
      @change="handleTitleInput"
    />

    <HugeRteTextArea
      :data-cy="'product-description-' + selectedLocaleCode"
      :name="`description-${selectedLocaleCode}`"
      :label="$t('DESCRIPTION')"
      :value="descriptions[selectedLocaleCode]"
      preset="product"
      :disabled="isDisabled"
      @input="handleDescriptionInput"
      @onAi="openAiDescriptionModal"
    />

    <Ai
      v-if="isAiTitleModalOpen"
      :localized-content="titles"
      :selected-locale="selectedLocale"
      :field-name="$t('TITLE')"
      @onClose="closeAiTitleModal"
      @onSubmitText="handleTitleInputAi"
    />

    <Ai
      v-if="isAiDescriptionModalOpen"
      :localized-content="descriptions"
      :selected-locale="selectedLocale"
      :field-name="$t('DESCRIPTION')"
      @onClose="closeAiDescriptionModal"
      @onSubmitText="handleDescriptionInputAi"
    />
  </div>
</template>

<script>
import Ai from '@/components/generics/ai/Ai.vue'
import BaseTextInput from '@/components/generics/base/BaseTextInput.vue'
import HugeRteTextArea from '@/components/generics/HugeRteTextArea.vue'
import { sanitizeText } from '@/helpers/textSanitizerHelpers'
import { useCurrentCompany } from '@/hooks/swr/useCurrentCompany'

export default {
  components: {
    Ai,
    BaseTextInput,
    HugeRteTextArea,
  },
  props: {
    titles: {
      type: Object,
      required: true,
      default: () => {},
    },

    descriptions: {
      type: Object,
      required: true,
      default: () => {},
    },

    locales: {
      type: Array,
      required: true,
    },

    selectedLocaleCode: {
      type: String,
      required: true,
    },

    isDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['onUpdateFormLocaleTitle', 'onUpdateFormLocaleDescription'],
  setup() {
    const { data: currentCompany } = useCurrentCompany()
    return { currentCompany }
  },

  data() {
    return {
      isAiTitleModalOpen: false,
      isAiDescriptionModalOpen: false,
    }
  },

  computed: {
    selectedLocale() {
      return this.locales.find(
        (locale) => locale.code === this.selectedLocaleCode
      )
    },

    hasCompanyAi() {
      return this.currentCompany.ai_features_enabled
    },
  },

  methods: {
    handleTitleInputAi(value) {
      this.closeAiTitleModal()
      this.handleTitleInput(value)
    },

    handleDescriptionInputAi(value) {
      this.closeAiDescriptionModal()
      this.handleDescriptionInput(value)
    },

    handleTitleInput(value) {
      this.$emit('onUpdateFormLocaleTitle', {
        locale: this.selectedLocaleCode,
        value: sanitizeText(value),
      })
    },

    handleDescriptionInput(value) {
      this.$emit('onUpdateFormLocaleDescription', {
        locale: this.selectedLocaleCode,
        value,
      })
    },

    openAiTitleModal() {
      this.isAiTitleModalOpen = true
    },

    closeAiTitleModal() {
      this.isAiTitleModalOpen = false
    },

    openAiDescriptionModal() {
      this.isAiDescriptionModalOpen = true
    },

    closeAiDescriptionModal() {
      this.isAiDescriptionModalOpen = false
    },
  },
}
</script>

<style lang="scss" scoped>
.description-container {
  display: flex;
  flex-direction: column;
  gap: $spacing-s;
  width: 100%;

  &__title-input {
    width: 100%;
  }
}
</style>
